import React, { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { getCampaigns, deleteCampaign } from '../api/auth';
import { showErrorMsg, showSuccessMsg } from '../helpers/message';
import { showLoading } from '../helpers/loading';
import { isAuthenticated } from "../helpers/auth";
import Tooltip from './Tooltip';
import moment from 'moment';

const Campaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCampaigns, setTotalCampaigns] = useState(0);
    const limit = 20;

    useEffect(() => {
        fetchCampaigns(currentPage);
    }, [currentPage]);

    const fetchCampaigns = async (page = 1) => {
        try {
            setLoading(true);
            setError('');
            const response = await getCampaigns(page, limit);
            
            if (response.campaigns && Array.isArray(response.campaigns)) {
                setCampaigns(response.campaigns);
                setTotalPages(response.pagination.totalPages);
                setTotalCampaigns(response.pagination.totalCampaigns);
                setCurrentPage(response.pagination.currentPage);
            } else if (Array.isArray(response)) {
                // Eski API formatı için fallback
                setCampaigns(response);
                setTotalCampaigns(response.length);
                setTotalPages(1);
            } else {
                console.error('Expected campaigns array, but got:', response);
                setError('Unexpected data format received from server');
            }
        } catch (err) {
            console.error('Error fetching campaigns:', err);
            setError(err.message || 'Error fetching campaigns. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (campaignId) => {
        if (window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
            try {
                await deleteCampaign(campaignId);
                setSuccessMsg('Campaign deleted successfully');
                setTimeout(() => setSuccessMsg(''), 3000);
                fetchCampaigns(currentPage);
            } catch (err) {
                console.error('Error deleting campaign:', err);
                setError(err.message || 'Error deleting campaign. Please try again.');
            }
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <nav>
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>
                                    1
                                </button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages.map(page => (
                        <li 
                            key={page} 
                            className={`page-item ${currentPage === page ? 'active' : ''}`}
                        >
                            <button 
                                className="page-link" 
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        </li>
                    ))}
                    {endPage < totalPages && (
                        <>
                            {endPage < totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                                    {totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (loading) return showLoading();

    if (error) return showErrorMsg(error);

    return (
        <Fragment>
            <header className="bg-surface-primary border-bottom pt-6 mb-5">
                <div className="container-fluid">
                    <div className="mb-npx">
                        <div className="row align-items-center">
                            <div className="col-sm-6 col-12 mb-4 mb-sm-0">
                                <h1 className="h2 mb-0 ls-tight">Campaigns</h1>
                            </div>
                            <div className="col-sm-6 col-12 text-sm-end">
                                <div className="mx-n1">
                                    <a href="/e/bulk" className="btn d-inline-flex btn-sm btn-primary mx-1">
                                        <span className="pe-2">
                                            <i className="fa-solid fa-plus"></i>
                                        </span>
                                        <span>Create Campaign</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                        <ul className="nav nav-tabs mt-4 overflow-x border-0"></ul>
                    </div>
                </div>
            </header>
            <main className="py-6 bg-surface-secondary">
                <div className="container-fluid">
                    {successMsg && showSuccessMsg(successMsg)}
                    {campaigns.length === 0 ? (
                        <>
                            <div className="row p-3">
                                <div className="mb-1 px-5">
                                    <h4 className="mb-1">You have not yet created any campaign.</h4>
                                </div>
                                <img className="px-5 pr-5" src="../images/content.jpg" alt=""/>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="card shadow border-0 mb-7 mt-7">
                                <div className="card-header">
                                    <h5 className="mb-0">Campaigns</h5>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover table-nowrap">
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col" className="w-50">Campaigns</th>
                                                <th scope="col" className="w-10">Total Links</th>
                                                <th scope="col" className="w-10">Total Clicks</th>
                                                <th scope="col" className="w-30"></th>
                                                <th scope="col" className="w-10">Date/Creator</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map(campaign => (
                                                <tr key={campaign._id}>
                                                    <td>
                                                        <div className="row mb-2 ms-1">
                                                            <div className="ml-2">
                                                                <b>&nbsp; {campaign.name}</b>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <a href={`/campaign/${campaign._id}`} className="btn btn-sm btn-neutral border-base mx-1">
                                                            <i className="fas fa-link"></i> {campaign.totalLinks?.toLocaleString('tr') || 0}
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <a href={`/campaign/${campaign._id}`} className="btn btn-sm btn-neutral border-base mx-1">
                                                            <i className="fa-solid fa-chart-simple"></i> {campaign.totalClicks?.toLocaleString('tr') || 0}
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <a href={`/campaign/${campaign._id}`} className="btn d-inline-flex btn-sm btn-neutral border-base mx-1">
                                                            <span className="pe-2">
                                                                <i className="fas fa-eye"></i>
                                                            </span>
                                                            <span>View Detail</span>
                                                        </a>
                                                        <button 
                                                            onClick={() => handleDelete(campaign._id)} 
                                                            className="btn btn-sm btn-neutral border-base mx-1"
                                                        >
                                                            <i className="fa-solid fa-trash"></i>
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div className="row mb-2">
                                                            <div className="ml-2 text-center">
                                                                <Tooltip content={moment(campaign.createdAt).format('MMMM Do YYYY, h:mm:ss a')} direction="top" delay="0">
                                                                    {moment(campaign.createdAt).fromNow()}
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                        <div className="row mb-2">
                                                            <div className="ml-2 text-center">
                                                                <span className="badge rounded-pill bg-dark">{campaign.creator}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="card-footer border-0 py-5">
                                    <div className="row align-items-center">
                                        <div className="col-sm-6">
                                            <span className="text-muted text-sm">
                                                Showing page {currentPage} of {totalPages} ({totalCampaigns} total campaigns)
                                            </span>
                                        </div>
                                        <div className="col-sm-6">
                                            {renderPagination()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </Fragment>
    );
};

export default Campaigns;